import React, { useCallback, useRef } from 'react';
import { useThemeChanger } from 'cityads-v3/hooks/theme'
import classes from './Firano.scss';

import Button from '@mui/material/Button';

import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import php from 'react-syntax-highlighter/dist/esm/languages/hljs/php';
import light from 'react-syntax-highlighter/dist/esm/styles/hljs/vs';
import dark from 'react-syntax-highlighter/dist/esm/styles/hljs/sunburst';

SyntaxHighlighter.registerLanguage('php', php);

type CodeType = 'json' | 'full'

type CodeExampleProps = {
  product_group: string,
  offers: number[],
  exclude_offer_ids: number[],
  sandbox: boolean,
  json: unknown,
  t: (key: string) => string,
  onCopy: (type: CodeType) => void
}

export default function CodeExample({
  exclude_offer_ids,
  json,
  offers,
  onCopy,
  product_group,
  sandbox,
  t
}: CodeExampleProps): JSX.Element {
  const exampleCodeRef = useRef<HTMLDivElement>(null);
  const exampleJSONRef = useRef<HTMLDivElement>(null);
  const themeChanger = useThemeChanger();
  const style = themeChanger.theme.palette.mode === 'dark' ? dark : light

  const copyExample = useCallback((ref: React.MutableRefObject<HTMLDivElement>, type: CodeType) => () => {
    const selection = window && window.getSelection();
    if (selection) {
      selection.selectAllChildren(ref.current);
      document.execCommand('copy');
      onCopy?.(type);
    }
  }, [onCopy])

  return (
    <div ref={exampleCodeRef}>
      <div
        ref={exampleJSONRef}
      >
        <SyntaxHighlighter
          language="php"
          style={style}
          className={classes.exampleCode}
        >
          {`$applicationData = '${JSON.stringify(json)}';`}
        </SyntaxHighlighter>
        <Button
          variant="contained"
          color="primary"
          onClick={copyExample(exampleJSONRef, 'json')}
        >
          {t('blockExample.copyJSON')}
        </Button>
      </div>
      <div>
        <SyntaxHighlighter
          language="php"
          style={style}
          className={classes.exampleCode}
        >
          {CodeExamplePHP({ sandbox, t, product_group, offers, exclude_offer_ids })}
        </SyntaxHighlighter>

        <Button
          variant="contained"
          color="primary"
          onClick={copyExample(exampleCodeRef, 'full')}
        >
          {t('blockExample.copyCode')}
        </Button>
      </div>
    </div>
  )
}

type CodeExamplePHPProps = Pick<
  CodeExampleProps,
  'sandbox' | 't' | 'product_group'
  | 'offers' | 'exclude_offer_ids'
>

export const CodeExamplePHP = ({ sandbox, t, product_group = '', offers = [], exclude_offer_ids = [] }: CodeExamplePHPProps): string => (
  `$queryParams = [
    'wm_id' => {${t('blockExample.yourWmid')}},
    'token' => {${t('blockExample.yourToken')}},
    'product_group' => ${JSON.stringify(product_group)},
    'offers' => ${JSON.stringify(offers)},
    'exclude_offer_ids' => ${JSON.stringify(exclude_offer_ids)},
    'sandbox' => ${sandbox ? 1 : 0}
];

$url = 'https://firano.cityads.com/api/rest/v2/applications.json';

$headers = [
    'Content-Type: application/json'
];

$curl = curl_init($url . '?' . http_build_query($queryParams));
curl_setopt($curl, CURLOPT_TIMEOUT, 50);
curl_setopt($curl, CURLOPT_POST, true);
curl_setopt($curl, CURLOPT_HTTPHEADER, $headers);
curl_setopt($curl, CURLOPT_POSTFIELDS, $applicationData);
curl_setopt($curl, CURLOPT_FOLLOWLOCATION, 1);
curl_setopt($curl, CURLOPT_MAXREDIRS, 3);
curl_setopt($curl, CURLOPT_RETURNTRANSFER, 1);
curl_setopt($curl, CURLOPT_HEADER, 1);

$response = curl_exec($curl);

var_dump($response); exit;`
);
